import axios from "axios";

import { resolveApiBase } from "../../utils/apiBase";
import { createClientRequestId } from "../../utils/clientRequestId";
import { BUSINESS_SESSION_KEY, refreshAccessToken } from "../../services/sessionRenewal";

const API_BASE = resolveApiBase();

function readBusinessToken() {
  try {
    const raw = localStorage.getItem(BUSINESS_SESSION_KEY);
    const session = raw ? JSON.parse(raw) : null;
    return session?.token || null;
  } catch {
    return null;
  }
}

export const riderApiClient = axios.create({
  baseURL: API_BASE || undefined,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT || 15000),
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Portal": "business",
  },
});

riderApiClient.interceptors.request.use((config) => {
  const token = readBusinessToken();
  if (!token) {
    const error = new Error("Sign in to continue to the rider workspace.");
    error.code = "RIDER_BUSINESS_SESSION_REQUIRED";
    throw error;
  }

  config.headers = config.headers || {};
  config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Portal"] = "business";
  return config;
});

function unwrap(response) {
  return response.data && Object.prototype.hasOwnProperty.call(response.data, "data")
    ? response.data.data
    : response.data;
}

async function riderRequest(config, { allowRefresh = true } = {}) {
  try {
    return unwrap(await riderApiClient(config));
  } catch (error) {
    // An expired access token is routine on a long trip. One refresh, one
    // retry - and never a second, or a genuinely dead session would loop.
    if (error?.response?.status === 401 && allowRefresh) {
      const failedToken = String(error.config?.headers?.Authorization || "").replace(/^Bearer /, "") || null;
      const token = await refreshAccessToken("business", { failedToken });
      if (token) {
        return riderRequest(
          { ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } },
          { allowRefresh: false },
        );
      }
    }

    if (error?.response?.status === 401) {
      const sentAuthorization = error.config?.headers?.Authorization;
      const currentToken = readBusinessToken();
      // A late response from an old session must not sign out a newer login.
      if (currentToken && sentAuthorization === `Bearer ${currentToken}`) {
        localStorage.removeItem(BUSINESS_SESSION_KEY);
        window.dispatchEvent(new StorageEvent("storage", { key: BUSINESS_SESSION_KEY }));
      }
    }
    if (error?.code === "RIDER_BUSINESS_SESSION_REQUIRED") throw error;
    const wrapped = new Error(
      error?.response?.data?.message || error?.message || "Rider request failed",
    );
    wrapped.status = error?.response?.status || null;
    wrapped.code = error?.response?.data?.code || error?.code || "RIDER_REQUEST_FAILED";
    wrapped.details = error?.response?.data?.errors || null;
    throw wrapped;
  }
}

function requireBusinessId(value) {
  const businessId = Number(value);
  if (!Number.isInteger(businessId) || businessId <= 0) {
    const error = new Error("Choose a workplace before continuing.");
    error.code = "RIDER_BUSINESS_CONTEXT_REQUIRED";
    throw error;
  }
  return businessId;
}

function requirePositiveId(value, label) {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    const error = new Error(`Choose a valid ${label} before continuing.`);
    error.code = "RIDER_REQUEST_INVALID";
    throw error;
  }
  return id;
}

function requireVersion(value, label = "version") {
  const version = Number(value);
  if (!Number.isInteger(version) || version < 1) {
    const error = new Error(`The ${label} is no longer current. Refresh and try again.`);
    error.code = "RIDER_REQUEST_INVALID";
    throw error;
  }
  return version;
}

function requireShipmentIds(value) {
  const ids = Array.isArray(value) ? value.map((entry) => Number(entry)) : [];
  if (!ids.length) throw new Error("Choose at least one parcel to hand over.");
  if (ids.some((id) => !Number.isInteger(id) || id < 1)) throw new Error("A parcel reference is invalid.");
  if (new Set(ids).size !== ids.length) throw new Error("The same parcel cannot be listed twice.");
  return ids.slice().sort((a, b) => a - b);
}

export const RIDER_PROOF_MAX_BYTES = 10 * 1024 * 1024;
export const RIDER_PROOF_MIME_TYPES = Object.freeze(["image/jpeg", "image/png", "image/webp"]);

export function validateRiderProofFile(file) {
  if (!file) throw new Error("Choose a proof photo before continuing.");
  if (!RIDER_PROOF_MIME_TYPES.includes(String(file.type || "").toLowerCase())) {
    throw new Error("Choose a JPEG, PNG, or WebP photo.");
  }
  if (!Number.isFinite(Number(file.size)) || Number(file.size) <= 0) {
    throw new Error("The selected photo is empty.");
  }
  if (Number(file.size) > RIDER_PROOF_MAX_BYTES) {
    throw new Error("The proof photo must be 10 MB or smaller.");
  }
  return file;
}

function requireTextLength(value, label, maximum, { required = false, minimum = 1 } = {}) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if ((required && text.length < minimum) || text.length > maximum) {
    throw new Error(`${label} must be ${required ? `${minimum}-${maximum}` : `at most ${maximum}`} characters.`);
  }
  return text || null;
}

export const riderApi = Object.freeze({
  departPickup(businessId, runId, input) {
    return riderRequest({ url:`/api/rider/delivery/runs/${requirePositiveId(runId,'trip')}/depart-pickup`, method:'POST',
      data:{businessId:requireBusinessId(businessId),expectedVersion:requireVersion(input.expectedVersion),location:input.location},
      headers:{'Idempotency-Key':input.idempotencyKey} });
  },
  arrivePickup(businessId, runId, input) {
    return riderRequest({ url:`/api/rider/delivery/runs/${requirePositiveId(runId,'trip')}/arrive-pickup`, method:'POST',
      data:{businessId:requireBusinessId(businessId),expectedVersion:requireVersion(input.expectedVersion),location:input.location},
      headers:{'Idempotency-Key':input.idempotencyKey} });
  },
  getContext(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/context",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  getDuty(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/duty",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  updateDuty(businessId, { dutyState, expectedVersion }, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/duty",
      method: "PATCH",
      data: {
        businessId: requireBusinessId(businessId),
        dutyState,
        expectedVersion,
      },
      signal: options.signal,
    });
  },

  getAssignments(businessId, { status, cursor, signal } = {}) {
    return riderRequest({
      url: "/api/rider/delivery/assignments",
      method: "GET",
      params: {
        businessId: requireBusinessId(businessId),
        ...(status ? { status } : {}),
        ...(cursor ? { cursor } : {}),
      },
      signal,
    });
  },

  getAssignment(businessId, assignmentId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/assignments/${Number(assignmentId)}`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  // Target-v2 only. A Rider reports facts about a committed Assignment; this is
  // not a decline and never releases the work. Only a server-established
  // deterministic condition or an authorized Business resolution can do that.
  reportAssignmentProblem(businessId, assignmentId, {
    expectedAssignmentVersion,
    reasonCode,
    note,
    location,
    idempotencyKey,
  } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/assignments/${requirePositiveId(assignmentId, 'assignment')}/problems`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedAssignmentVersion: requireVersion(expectedAssignmentVersion),
        reasonCode,
        ...(note ? { note } : {}),
        ...(location ? { location } : {}),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("assignment_problem") },
      signal: options.signal,
    });
  },


  // Advisory step directions from where the rider is now. Read-only: it never
  // affects the trip, so a failure here is quiet.
  getRunGuidance(businessId, runId, position, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, 'trip')}/guidance`,
      method: 'GET',
      params: {
        businessId,
        latitude: position?.latitude,
        longitude: position?.longitude,
      },
      signal: options.signal,
    });
  },

  getCurrentRun(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/runs/current",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  getRun(businessId, runId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  // Scanning the store's live code is the receipt: the server records custody of
  // the whole released list in the same command. `labelChecks` carries the
  // Rider's optional per-parcel label scans as evidence.
  verifyHandoffChallenge(businessId, runId, {
    expectedRunVersion, manifestVersion, credential, method, location, labelChecks, idempotencyKey,
  } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/handoff-challenge/verify`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedRunVersion: requireVersion(expectedRunVersion, "run version"),
        manifestVersion: requireVersion(manifestVersion, "manifest version"),
        credential: String(credential || '').trim(),
        method: String(method || '').trim().toUpperCase(),
        ...(location ? { location } : {}),
        ...(Array.isArray(labelChecks) && labelChecks.length ? { labelChecks } : {}),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("handoff_verify") },
      signal: options.signal,
    });
  },

  startRun(businessId, runId, { expectedVersion, idempotencyKey } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/start`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(expectedVersion, "run version"),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("run_start"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  activateStop(businessId, stopId, {
    expectedVersion,
    expectedRunVersion,
    idempotencyKey,
  } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/stops/${requirePositiveId(stopId, "stop")}/activate`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(expectedVersion, "stop version"),
        expectedRunVersion: requireVersion(expectedRunVersion, "run version"),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("stop_activate"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  startAttempt(businessId, stopId, { expectedVersion, idempotencyKey } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/stops/${requirePositiveId(stopId, "stop")}/attempts`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(expectedVersion, "stop version"),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("attempt_start"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  arriveStop(businessId, stopId, {
    attemptId,
    expectedVersion,
    expectedAttemptVersion,
    expectedRunVersion,
    idempotencyKey,
  } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/stops/${requirePositiveId(stopId, "stop")}/arrive`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        attemptId: requirePositiveId(attemptId, "delivery attempt"),
        expectedVersion: requireVersion(expectedVersion, "stop version"),
        expectedAttemptVersion: requireVersion(expectedAttemptVersion, "attempt version"),
        expectedRunVersion: requireVersion(expectedRunVersion, "run version"),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("stop_arrive"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  completeAttempt(businessId, attemptId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/attempts/${requirePositiveId(attemptId, "delivery attempt")}/complete`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        proofId: requirePositiveId(payload.proofId, "delivery proof"),
        expectedAttemptVersion: requireVersion(payload.expectedAttemptVersion, "attempt version"),
        expectedStopVersion: requireVersion(payload.expectedStopVersion, "stop version"),
        expectedShipmentVersion: requireVersion(payload.expectedShipmentVersion, "shipment version"),
        expectedAssignmentVersion: requireVersion(payload.expectedAssignmentVersion, "assignment version"),
        expectedRunVersion: requireVersion(payload.expectedRunVersion, "run version"),
        // Legacy v1 only. WP6 target completion refuses a cash payload outright
        // (COD_COLLECTION_SEPARATE_REQUIRED), because completion must never be
        // the thing that records money.
        ...(payload.codCollection ? {
          codCollection: {
            amountCents: requirePositiveId(payload.codCollection.amountCents, "cash amount"),
            expectedCodVersion: requireVersion(payload.codCollection.expectedCodVersion, "COD version"),
          },
        } : {}),
      },
      headers: { "Idempotency-Key": payload.idempotencyKey || createClientRequestId("attempt_complete"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  failAttempt(businessId, attemptId, payload = {}, options = {}) {
    const reason = String(payload.reason || "").trim().toUpperCase();
    const nextAction = String(payload.nextAction || "").trim().toUpperCase();
    const allowedReasons = ["RECIPIENT_UNAVAILABLE", "CUSTOMER_UNREACHABLE", "ADDRESS_NOT_FOUND", "CUSTOMER_REQUESTED_RESCHEDULE", "COD_NOT_PROVIDED", "UNSAFE_OR_INACCESSIBLE", "PARCEL_DAMAGED", "OTHER"];
    const allowedActions = ["SAME_RUN_RETRY", "RESCHEDULE", "RETURN_TO_DISPATCH", "CUSTODY_RETURN", "MANAGEMENT_REVIEW", "TERMINAL_FAILURE_REVIEW"];
    // WP6 target-v2 records facts only. The Rider no longer chooses the Stop's
    // outcome, so sending a disposition is refused by the server
    // (DELIVERY_DISPOSITION_SEPARATE_REQUIRED) rather than quietly ignored.
    const targetV2 = payload.workflowVersion != null && Number(payload.workflowVersion) === 2;
    if (!allowedReasons.includes(reason)) throw new Error("Choose a delivery problem.");
    if (!targetV2 && !allowedActions.includes(nextAction)) throw new Error("Choose what should happen next.");
    const explanation = requireTextLength(payload.explanation, "Explanation", 1000, {
      required: reason === "OTHER", minimum: 10,
    });
    const notes = requireTextLength(payload.notes, "Notes", 1000);
    if (!targetV2 && nextAction === "RESCHEDULE") {
      const before = new Date(payload.rescheduledNotBefore);
      const after = new Date(payload.rescheduledNotAfter);
      if (Number.isNaN(before.getTime()) || Number.isNaN(after.getTime()) || after <= before) {
        throw new Error("Choose a valid reschedule window.");
      }
    }
    return riderRequest({
      url: `/api/rider/delivery/attempts/${requirePositiveId(attemptId, "delivery attempt")}/fail`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        reason,
        explanation,
        notes,
        contactOutcomes: Array.isArray(payload.contactOutcomes) ? payload.contactOutcomes : [],
        locationEvidence: payload.locationEvidence || null,
        ...(targetV2 ? {} : {
          nextAction,
          rescheduledNotBefore: payload.rescheduledNotBefore || null,
          rescheduledNotAfter: payload.rescheduledNotAfter || null,
          custodyDisposition: payload.custodyDisposition || null,
        }),
        expectedAttemptVersion: requireVersion(payload.expectedAttemptVersion, "attempt version"),
        expectedStopVersion: requireVersion(payload.expectedStopVersion, "stop version"),
        expectedShipmentVersion: requireVersion(payload.expectedShipmentVersion, "shipment version"),
        expectedAssignmentVersion: requireVersion(payload.expectedAssignmentVersion, "assignment version"),
        expectedRunVersion: requireVersion(payload.expectedRunVersion, "run version"),
      },
      headers: { "Idempotency-Key": payload.idempotencyKey || createClientRequestId("attempt_fail"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },


  // WP6 target-v2: cash is its own command. Collecting it is not completing the
  // delivery, and completing the delivery never carries a cash payload.
  collectCod(businessId, attemptId, payload = {}, options = {}) {
    const netDueCents = requirePositiveId(payload.netDueCents, "amount due");
    const tenderedCents = requirePositiveId(payload.tenderedCents, "cash handed over");
    if (tenderedCents < netDueCents) {
      throw new Error("Cash received must cover the full amount due. Report a delivery problem for a short payment.");
    }
    return riderRequest({
      url: `/api/rider/delivery/attempts/${requirePositiveId(attemptId, "delivery attempt")}/cod-collection`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        codRecordId: requirePositiveId(payload.codRecordId, "cash record"),
        expectedCodVersion: requireVersion(payload.expectedCodVersion, "COD version"),
        netDueCents,
        tenderedCents,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("cod_collect"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  // WP6 target-v2 Physical Cash Return. The Rider asserts; the customer confirms.
  // No destination and no amount are sent: the server takes both from the
  // collection, so this client cannot express a partial or redirected return.
  assertCashReturn(businessId, attemptId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/attempts/${requirePositiveId(attemptId, "delivery attempt")}/cash-return`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedCodVersion: requireVersion(payload.expectedCodVersion, "COD version"),
        note: requireTextLength(payload.note, "Note", 1000),
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("cash_return"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  getCashReturns(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/cash-returns",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  redeemCashReturn(businessId, cashReturnId, payload = {}, options = {}) {
    const code = String(payload.code ?? "").trim();
    if (!/^\d{6}$/.test(code)) throw new Error("Enter the six-digit code from the customer's order.");
    return riderRequest({
      url: `/api/rider/delivery/cash-returns/${requirePositiveId(cashReturnId, "cash return")}/redeem`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(payload.expectedVersion, "cash return version"),
        code,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("cash_return_redeem"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  // WP7 target-v2 Parcel return and Emergency Recovery. Presenting moves no
  // custody: authorized staff confirm what they physically take, on their own
  // screen. Nothing here can name a receiver, and nothing here can say a Parcel
  // was handed over.
  getHeldParcels(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/held-parcels",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  openReturnSession(businessId, payload = {}, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/return-sessions",
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        deliveryRunId: requirePositiveId(payload.deliveryRunId, "trip"),
        presentedShipmentIds: requireShipmentIds(payload.presentedShipmentIds),
        note: payload.note ? requireTextLength(payload.note, "Note", 500) : undefined,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("return_open"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  openEmergencyRecovery(businessId, payload = {}, options = {}) {
    const reasonCode = String(payload.emergencyReasonCode || "").trim().toUpperCase();
    if (!reasonCode) throw new Error("Choose why you cannot continue this trip.");
    return riderRequest({
      url: "/api/rider/delivery/emergency-recoveries",
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        deliveryRunId: requirePositiveId(payload.deliveryRunId, "trip"),
        presentedShipmentIds: requireShipmentIds(payload.presentedShipmentIds),
        emergencyReasonCode: reasonCode,
        note: payload.note ? requireTextLength(payload.note, "Note", 500) : undefined,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("emergency_recovery"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  // WP8 target-v2 (dark). Handing the trip's cash to the counter. Submitting
  // moves no money: authorized staff confirm what they physically count, and
  // until they do the cash is still the Rider's.
  previewRunRemittance(businessId, runId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/remittance`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  submitRunRemittance(businessId, runId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/remittance`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        // Optional, and an agreement check rather than an input: if the screen
        // showed a different total from the one the server derives under lock,
        // the command refuses rather than accepting a stale number.
        acknowledgedAmountCents: payload.acknowledgedAmountCents == null
          ? undefined : Number(payload.acknowledgedAmountCents),
        notes: payload.notes ? requireTextLength(payload.notes, "Note", 1000) : undefined,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("run_remittance"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  submitEmergencyCashRecovery(businessId, runId, payload = {}, options = {}) {
    const reasonCode = String(payload.emergencyReasonCode || "").trim().toUpperCase();
    if (!reasonCode) throw new Error("Choose why you cannot finish this trip.");
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/emergency-cash-recovery`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        emergencyReasonCode: reasonCode,
        notes: payload.notes ? requireTextLength(payload.notes, "Note", 1000) : undefined,
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("emergency_cash"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  // Why this Rider is not being offered new work. Read-only: a hold is lifted by
  // resolving the cash discrepancy that caused it, never from this side.
  getMyWorkHolds(businessId, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/work-holds",
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  // Where to take what they are carrying, and who is authorized to receive it.
  getSettlementDestinations(businessId, runId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/settlement-destinations`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      signal: options.signal,
    });
  },

  abandonReturnSession(businessId, returnSessionId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/return-sessions/${requirePositiveId(returnSessionId, "return session")}/abandon`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(payload.expectedVersion, "return session version"),
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("return_abandon"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  previewRunCompletion(businessId, runId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/completion`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId) },
      headers: { ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  completeRun(businessId, runId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "trip")}/complete`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedRunVersion: requireVersion(payload.expectedRunVersion, "trip version"),
      },
      headers: {
        "Idempotency-Key": payload.idempotencyKey || createClientRequestId("run_complete"),
        ...(options.executionHeaders || {}),
      },
      signal: options.signal,
    });
  },

  reportExecutionProblem(businessId, runId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/problems`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        phase: String(payload.phase || "").trim().toUpperCase(),
        reasonCode: String(payload.reasonCode || "").trim().toUpperCase(),
        note: requireTextLength(payload.note, "Problem note", 1000, {
          required: String(payload.reasonCode || "").trim().toUpperCase() === "OTHER",
          minimum: 10,
        }),
        stopId: payload.stopId || null,
        attemptId: payload.attemptId || null,
        location: payload.location || null,
      },
      headers: { "Idempotency-Key": payload.idempotencyKey || createClientRequestId("execution_problem"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  getHistory(businessId, { cursor, signal } = {}) {
    return riderRequest({
      url: "/api/rider/delivery/history",
      method: "GET",
      params: {
        businessId: requireBusinessId(businessId),
        ...(cursor ? { cursor: requirePositiveId(cursor, "history page") } : {}),
      },
      signal,
    });
  },

  startTracking(businessId, runId, { expectedRunVersion, deviceInstanceId, idempotencyKey, recoverSessionId } = {}, options = {}) {
    return riderRequest({
      url: "/api/rider/delivery/tracking-sessions",
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        runId: requirePositiveId(runId, "run"),
        expectedRunVersion: requireVersion(expectedRunVersion, "run version"),
        deviceInstanceId: String(deviceInstanceId || "").trim(),
        ...(recoverSessionId ? {recoverSessionId} : {}),
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("tracking_start"), ...(options.executionHeaders || {}) },
      signal: options.signal,
    });
  },

  uploadTrackingLocations(businessId, sessionId, samples, trackingToken, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/tracking-sessions/${requirePositiveId(sessionId, "tracking session")}/locations:batch`,
      method: "POST",
      data: { businessId: requireBusinessId(businessId), samples },
      headers: { "X-Delivery-Tracking-Token": trackingToken },
      signal: options.signal,
    });
  },

  heartbeatTracking(businessId, sessionId, expectedVersion, trackingToken, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/tracking-sessions/${requirePositiveId(sessionId, "tracking session")}/heartbeat`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(expectedVersion, "tracking version"),
      },
      headers: { "X-Delivery-Tracking-Token": trackingToken },
      signal: options.signal,
    });
  },

  endTracking(businessId, sessionId, { expectedVersion, reason, idempotencyKey } = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/tracking-sessions/${requirePositiveId(sessionId, "tracking session")}/end`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId),
        expectedVersion: requireVersion(expectedVersion, "tracking version"),
        reason,
      },
      headers: { "Idempotency-Key": idempotencyKey || createClientRequestId("tracking_end") },
      signal: options.signal,
    });
  },

  getExecutionAuthority(businessId, runId, deviceInstanceId, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/execution-authority`,
      method: "GET",
      params: { businessId: requireBusinessId(businessId), deviceInstanceId: String(deviceInstanceId || "").trim() },
      signal: options.signal,
    });
  },

  claimExecutionAuthority(businessId, runId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/execution-authority/claim`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId), workflowVersion: 2,
        expectedRunVersion: requireVersion(payload.expectedRunVersion, "run version"),
        deviceInstanceId: String(payload.deviceInstanceId || "").trim(),
        ...(payload.correlationId ? { correlationId: payload.correlationId } : {}),
      },
      headers: { "Idempotency-Key": payload.idempotencyKey || createClientRequestId("execution_claim") },
      signal: options.signal,
    });
  },

  takeoverExecutionAuthority(businessId, runId, payload = {}, options = {}) {
    return riderRequest({
      url: `/api/rider/delivery/runs/${requirePositiveId(runId, "run")}/execution-authority/takeover`,
      method: "POST",
      data: {
        businessId: requireBusinessId(businessId), workflowVersion: 2,
        expectedRunVersion: requireVersion(payload.expectedRunVersion, "run version"),
        deviceInstanceId: String(payload.deviceInstanceId || "").trim(),
        takeoverAcknowledged: payload.takeoverAcknowledged === true,
        reasonCode: String(payload.reasonCode || "").trim(),
        reasonDetail: String(payload.reasonDetail || "").trim() || null,
        ...(payload.correlationId ? { correlationId: payload.correlationId } : {}),
      },
      headers: { "Idempotency-Key": payload.idempotencyKey || createClientRequestId("execution_takeover") },
      signal: options.signal,
    });
  },
});
